import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { UserProvider } from "@/contexts/UserContext";
import { UpsellProviderWrapper } from "@/components/providers/UpsellProviderWrapper";
import { LocationTrackingProvider } from "@/components/providers/LocationTrackingProvider";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { ScrollToTop } from "@/components/ScrollToTop";
import LoadingSpinner from "@/components/LoadingSpinner";
import { useNavigate } from "react-router-dom";

// Retry wrapper for lazy imports — handles stale chunk errors after deploys
// When a dynamic import fails (e.g. old chunk hash gone), reload the page once
function lazyWithRetry(importFn: () => Promise<any>) {
  return lazy(() =>
    importFn().catch((error: Error) => {
      // Only retry once per session to avoid infinite reload loops
      const hasRefreshed = sessionStorage.getItem('chunk-retry');
      if (!hasRefreshed) {
        sessionStorage.setItem('chunk-retry', 'true');
        window.location.reload();
        // Return a never-resolving promise while the page reloads
        return new Promise(() => {});
      }
      // If we already retried, clear the flag and throw the real error
      sessionStorage.removeItem('chunk-retry');
      throw error;
    })
  );
}

// Lazy-loaded pages — all wrapped with retry for stale chunk resilience
const Index = lazyWithRetry(() => import("./pages/Index"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const EnhancedSignup = lazyWithRetry(() => import("./pages/EnhancedSignup"));
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const AdminDashboard = lazyWithRetry(() => import("./pages/AdminDashboard"));
const MyTrucks = lazyWithRetry(() => import("./pages/MyTrucks"));
const Favorites = lazyWithRetry(() => import("./pages/Favorites"));
const Alerts = lazyWithRetry(() => import("./pages/Alerts"));
const PremiumNotifications = lazyWithRetry(() => import("./pages/PremiumNotifications"));
const ProfileSettings = lazyWithRetry(() => import("./pages/ProfileSettings"));
const MenuEditor = lazyWithRetry(() => import("./pages/MenuEditor"));
const Support = lazyWithRetry(() => import("./pages/Support"));
const Schedule = lazyWithRetry(() => import("./pages/Schedule"));
const Analytics = lazyWithRetry(() => import("./pages/Analytics"));
const Billing = lazyWithRetry(() => import("./pages/Billing"));
const EventOrganizerBilling = lazyWithRetry(() => import("./pages/EventOrganizerBilling"));
const UpgradeSuccess = lazyWithRetry(() => import("./pages/UpgradeSuccess"));
const UpgradeCancel = lazyWithRetry(() => import("./pages/UpgradeCancel"));
const SuccessBasedOnboarding = lazyWithRetry(() => import("./pages/SuccessBasedOnboarding"));
const ClaimTruck = lazyWithRetry(() => import("./pages/ClaimTruck"));
const ChoosePath = lazyWithRetry(() => import("./pages/ChoosePath"));
const HomeBase = lazyWithRetry(() => import("./pages/HomeBase"));
const EventRequests = lazyWithRetry(() => import("./pages/EventRequests"));
const EventProposals = lazyWithRetry(() => import("./pages/EventProposals"));
const EventDayDashboard = lazyWithRetry(() => import("./pages/EventDayDashboard"));
const EventAnalytics = lazyWithRetry(() => import("./pages/EventAnalytics"));
const TruckOpportunities = lazyWithRetry(() => import("./pages/TruckOpportunities"));
const Messages = lazyWithRetry(() => import("./pages/Messages"));
const OnboardingRefresh = lazyWithRetry(() => import("./pages/OnboardingRefresh"));
const OnboardingStripeSuccess = lazyWithRetry(() => import("./pages/OnboardingStripeSuccess"));
const OnboardingStripeSetup = lazyWithRetry(() => import("./pages/OnboardingStripeSetup"));
const EventBookingCheckout = lazyWithRetry(() => import("./pages/EventBookingCheckout"));
const EventBookingSuccess = lazyWithRetry(() => import("./pages/EventBookingSuccess"));
const CommissionDashboard = lazyWithRetry(() => import("./pages/CommissionDashboard"));
const OrganizerCommissions = lazyWithRetry(() => import("./pages/OrganizerCommissions"));
const AdminRefundDashboard = lazyWithRetry(() => import("./pages/AdminRefundDashboard"));
const AdminBookings = lazyWithRetry(() => import("./pages/AdminBookings"));
const AdminCommissions = lazyWithRetry(() => import("./pages/AdminCommissions"));
const PricingComparison = lazyWithRetry(() => import("./pages/PricingComparison"));
const VendorROICalculator = lazyWithRetry(() => import("./pages/VendorROICalculator"));
const EventOrganizerROICalculator = lazyWithRetry(() => import("./pages/EventOrganizerROICalculator"));
const PublicSupport = lazyWithRetry(() => import("./pages/PublicSupport"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazyWithRetry(() => import("./pages/TermsOfService"));
const PublicMap = lazyWithRetry(() => import("./pages/PublicMap"));

// Lazy-loaded components used only in routes
const VendorOnboardingWizardLazy = lazyWithRetry(() => import("./components/onboarding/VendorOnboardingWizard").then(m => ({ default: m.VendorOnboardingWizard })));
const OnboardingSuccess = lazyWithRetry(() => import("./pages/OnboardingSuccess").then(m => ({ default: m.OnboardingSuccess })));

// Wrapper component for VendorOnboarding with proper navigation
const VendorOnboardingWrapper = () => {
  const navigate = useNavigate();
  
  return (
    <VendorOnboardingWizardLazy 
      onComplete={() => navigate('/onboarding/success')} 
      onSkip={() => navigate('/dashboard')} 
    />
  );
};

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <UserProvider>
        <LocationTrackingProvider>
          <UpsellProviderWrapper>
          <Toaster />
          <Sonner />
          <BrowserRouter future={{ 
            v7_startTransition: true,
            v7_relativeSplatPath: true 
          }}>
          {/* Scroll restoration: Scrolls to top on route changes, or to hash anchors */}
          <ScrollToTop />
          <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/signup" element={<EnhancedSignup />} />
            <Route path="/choose-path" element={<ChoosePath />} />
            {/* New success-based onboarding */}
            <Route path="/vendor/pricing" element={<SuccessBasedOnboarding />} />
            {/* Old subscription selection - keeping for backwards compatibility */}
            <Route path="/subscription-selection" element={<SuccessBasedOnboarding />} />
            <Route path="/onboarding/vendor" element={<VendorOnboardingWrapper />} />
            <Route path="/onboarding/success" element={<OnboardingSuccess />} />
          <Route path="/onboarding/refresh" element={<OnboardingRefresh />} />
          <Route path="/onboarding/stripe-success" element={<OnboardingStripeSuccess />} />
          <Route path="/onboarding/stripe-setup" element={<OnboardingStripeSetup />} />
            {/* Protected Routes */}
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute requireRole="admin"><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/bookings" element={<ProtectedRoute requireRole="admin"><AdminBookings /></ProtectedRoute>} />
            <Route path="/admin/commissions" element={<ProtectedRoute requireRole="admin"><AdminCommissions /></ProtectedRoute>} />
            <Route path="/dashboard/my-trucks" element={<ProtectedRoute><MyTrucks /></ProtectedRoute>} />
            <Route path="/claim-truck" element={<ProtectedRoute><ClaimTruck /></ProtectedRoute>} />
            <Route path="/dashboard/home-base" element={<ProtectedRoute><HomeBase /></ProtectedRoute>} />
            <Route path="/dashboard/favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
            <Route path="/dashboard/alerts" element={<ProtectedRoute><Alerts /></ProtectedRoute>} />
            <Route path="/premium-notifications" element={<ProtectedRoute><PremiumNotifications /></ProtectedRoute>} />
            <Route path="/dashboard/profile" element={<ProtectedRoute><ProfileSettings /></ProtectedRoute>} />
            <Route path="/dashboard/menu" element={<ProtectedRoute><MenuEditor /></ProtectedRoute>} />
            <Route path="/dashboard/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
            <Route path="/dashboard/schedule" element={<ProtectedRoute><Schedule /></ProtectedRoute>} />
            <Route path="/dashboard/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
            <Route path="/dashboard/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
            <Route path="/event-requests" element={<ProtectedRoute><EventRequests /></ProtectedRoute>} />
            <Route path="/event-requests/:id/proposals" element={<ProtectedRoute><EventProposals /></ProtectedRoute>} />
            <Route path="/event-requests/:id/dashboard" element={<ProtectedRoute><EventDayDashboard /></ProtectedRoute>} />
            <Route path="/event-requests/:id/analytics" element={<ProtectedRoute><EventAnalytics /></ProtectedRoute>} />
            <Route path="/truck-opportunities" element={<ProtectedRoute><TruckOpportunities /></ProtectedRoute>} />
            <Route path="/event-booking/checkout" element={<ProtectedRoute><EventBookingCheckout /></ProtectedRoute>} />
            <Route path="/event-booking/success" element={<ProtectedRoute><EventBookingSuccess /></ProtectedRoute>} />
            <Route path="/dashboard/commissions" element={<ProtectedRoute><CommissionDashboard /></ProtectedRoute>} />
            <Route path="/dashboard/organizer-commissions" element={<ProtectedRoute><OrganizerCommissions /></ProtectedRoute>} />
            <Route path="/dashboard/admin/refunds" element={<ProtectedRoute><AdminRefundDashboard /></ProtectedRoute>} />
            
            <Route path="/dashboard/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
            <Route path="/event-organizer/billing" element={<ProtectedRoute><EventOrganizerBilling /></ProtectedRoute>} />
            <Route path="/upgrade/success" element={<UpgradeSuccess />} />
            <Route path="/upgrade/cancel" element={<UpgradeCancel />} />
            
            {/* Marketing & Public Pages */}
            <Route path="/map" element={<PublicMap />} />
            <Route path="/pricing-comparison" element={<PricingComparison />} />
            <Route path="/vendor-roi-calculator" element={<VendorROICalculator />} />
            <Route path="/event-roi-calculator" element={<EventOrganizerROICalculator />} />
            <Route path="/support" element={<PublicSupport />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
        </UpsellProviderWrapper>
        </LocationTrackingProvider>
      </UserProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;